/**
 * The household and options every Support at home figure is priced on — ONE place (rebuild M3,
 * 19 Oct 2026), so step 2's four numbers, step 1's money panel and the engine's plans cannot be
 * priced on different households.
 *
 *  - Step 1's question "Is Colin moving into aged care?" (goals.sahAgedCareMove). "Yes": the person
 *    moving has no Support at home services priced, and the chosen aged care plan is included — its
 *    room deposit paid, its living costs, its residency (illness-separated), and its yearly fees
 *    charged (pack B.SAH.6). "No" or "Not sure": both at home, as today.
 *  - The support start date rides the same options bag (the aged care "entry timing belongs to the
 *    case" rule).
 *
 * THE WHOLE AGED CARE PLAN (owner, 24 Oct 2026: "carry the whole aged care plan into Support at Home
 * ... ultimately it's their reality"). The household is priced as it stands once the chosen aged care
 * plan has been carried out — the same rebuild the forms use for "the position after the plan"
 * (features/forms/planPosition.ts): the deposit paid, and every other piece of that plan too (a home
 * or property sold, gifts, a funeral bond, an annuity, debts cleared, a second room). Until then only
 * the deposit was applied. The deposit-only path stays as the fallback when the plan cannot be rebuilt.
 */
import { applyRadFundingToFactFind, extractAgedCareIntegrationDetails, type AgedCareIntegrationDetails, type SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import { getAgedCareModellerResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { positionAfterPlan, type SavedPlan } from '../forms/planPosition.ts';

export type SahMoveAnswer = 'no' | 'yes' | 'unsure';

/** Step 1's answer, or null when the question does not arise (no aged care plan for a partner). */
export function sahMoveAnswer(factFind: DraftFactFind | null | undefined): SahMoveAnswer | null {
    const a = (factFind?.goals as { sahAgedCareMove?: string } | undefined)?.sahAgedCareMove;
    return a === 'yes' || a === 'no' || a === 'unsure' ? a : null;
}

/** Who the aged care goal is for — the person who would move ('client' | 'partner'), or null. */
export function sahEnteringPerson(factFind: DraftFactFind | null | undefined): 'client' | 'partner' | null {
    if (factFind?.adviceFor !== 'couple') return null;
    const g = (factFind?.goals || {}) as { aged_care?: boolean; aged_care_for?: string };
    if (!g.aged_care) return null;
    return g.aged_care_for === 'partner' ? 'partner' : g.aged_care_for === 'client' ? 'client' : null;
}

/** The service plan as priced: with "Yes — X is moving", X has no Support at home services. */
export function sahPricedServicePlan<T extends { client?: unknown; partner?: unknown }>(plan: T, factFind: DraftFactFind | null | undefined): T {
    if (sahMoveAnswer(factFind) !== 'yes') return plan;
    const who = sahEnteringPerson(factFind);
    return who ? ({ ...plan, [who]: {} } as T) : plan;
}

/** The chosen aged care plan on file (or the only one), as the linked plans read it. */
export function chosenAgedCarePlanOf(saved: ReadonlyArray<SavedStrategy> | null | undefined): SavedStrategy | null {
    const list = (saved || []).filter((s) => s?.type === 'aged_care');
    return list.find((s) => (s as { chosen?: boolean }).chosen) ?? (list.length === 1 ? list[0] : null);
}

/** The residency the linked plans are assessed on: the aged care plan's own, else the profile's. */
export function linkedResidencyOverridesOf(integration: AgedCareIntegrationDetails | null, factFind: DraftFactFind): Record<string, string> {
    if (!integration) return {};
    const from = (integration.strategyOptions || {}) as { clientResidencyOverride?: string; partnerResidencyOverride?: string };
    const overrides = {
        ...(from.clientResidencyOverride ? { clientResidencyOverride: from.clientResidencyOverride } : {}),
        ...(from.partnerResidencyOverride ? { partnerResidencyOverride: from.partnerResidencyOverride } : {}),
    };
    return Object.keys(overrides).length ? overrides : (getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind) as Record<string, string>);
}

/** The options bag: the start date, and in the linked case the residency and the aged care fees. */
export function sahContextOptions(args: { supportStartDate?: string; linked: AgedCareIntegrationDetails | null; overrides: Record<string, string> }): { strategyOptions?: Record<string, unknown> } {
    // A past date is "already receiving support" — the same collapse Optimise makes (sahStartDate.test.ts).
    const future = args.supportStartDate && args.supportStartDate >= new Date().toISOString().slice(0, 10) ? args.supportStartDate : '';
    const timing = future ? { careStartDate: future } : {};
    if (!args.linked) return Object.keys(timing).length > 0 ? { strategyOptions: timing } : {};
    const fees = Number((args.linked as { annualCost?: number }).annualCost) || 0;
    return { strategyOptions: { ...args.overrides, ...timing, ...(fees > 0 ? { linkedAgedCareAnnualCost: fees } : {}) } };
}

/**
 * The household once the chosen aged care plan has been carried out, or null when there is none or it
 * cannot be rebuilt (then `reason` says why, and the deposit alone is applied as before).
 */
export async function sahLinkedPosition(factFind: DraftFactFind, linkedRecord: SavedStrategy | null): Promise<{ factFind: DraftFactFind | null; caveats: string[]; reason?: string }> {
    if (!linkedRecord) return { factFind: null, caveats: [] };
    const r = await positionAfterPlan(factFind, linkedRecord as unknown as SavedPlan);
    return r.ok ? { factFind: r.factFind, caveats: r.caveats } : { factFind: null, caveats: [], reason: r.reason };
}

/**
 * The household priced: in the linked case the aged care plan carried out (`positioned`, from
 * sahLinkedPosition) — or, when that is not to hand, its deposit paid — and the living costs the plan's.
 */
export function sahBaselineFactFind(factFind: DraftFactFind, linked: AgedCareIntegrationDetails | null, structureFunding?: string, positioned?: DraftFactFind | null): DraftFactFind {
    let ff: DraftFactFind = structuredClone(linked && positioned ? positioned : factFind);
    if (structureFunding && (factFind?.structures || []).length > 0) ff.structureFundingInSuggestions = structureFunding as never;
    if (linked) {
        if (linked.radFunding && !positioned) ff = applyRadFundingToFactFind(ff, linked.radFunding);
        if (linked.livingExpenses !== undefined && linked.livingExpenses !== null) {
            ff.expenses = { ...(ff.expenses || {}), livingExpenses: linked.livingExpenses };
        }
    }
    return ff;
}

/** Everything at once, for a screen that prices the do-nothing position (steps 1 and 2). `positioned` is the
 *  aged care plan carried out (sahLinkedPosition), when the screen has it. */
export function sahEngineContext(args: { factFind: DraftFactFind; savedStrategies: ReadonlyArray<SavedStrategy>; supportStartDate?: string; positioned?: DraftFactFind | null }) {
    const linkedRecord = sahMoveAnswer(args.factFind) === 'yes' ? chosenAgedCarePlanOf(args.savedStrategies) : null;
    const linked = linkedRecord ? extractAgedCareIntegrationDetails(linkedRecord) : null;
    const overrides = linkedResidencyOverridesOf(linked, args.factFind);
    const ctx = sahContextOptions({ supportStartDate: args.supportStartDate, linked, overrides });
    return {
        linked,
        linkedRecord,
        baselineFactFind: sahBaselineFactFind(args.factFind, linked, args.factFind?.structureFundingInSuggestions as string | undefined, args.positioned),
        evaluationOptions: { strategyOptions: ctx.strategyOptions || {}, contributionOptions: ctx },
    };
}

/**
 * The linked aged care plan as the screens and the advice document describe it (24 Oct 2026): the plan,
 * the profile as entered, and the household the figures are priced on once the plan is carried out.
 */
export async function sahLinkedCareFor(factFind: DraftFactFind, record: SavedStrategy | null): Promise<{
    record: SavedStrategy; details: AgedCareIntegrationDetails | null; before: DraftFactFind; after: DraftFactFind; whole: boolean; caveats: string[]; reason?: string;
} | null> {
    if (!record) return null;
    const details = extractAgedCareIntegrationDetails(record);
    const pos = await sahLinkedPosition(factFind, record);
    return { record, details, before: factFind, after: sahBaselineFactFind(factFind, details, undefined, pos.factFind), whole: !!pos.factFind, caveats: pos.caveats, reason: pos.reason };
}

/**
 * How a hand-built plan, and its "doing nothing", are projected — the engine's way (25 Oct 2026, pack
 * B.SAH.6): with a partner's aged care plan linked, the Support at home contribution and that plan's yearly
 * fees are ONE integrated cost, and the care-cost bag carries the contribution alone. Handing the fees over
 * as part of a bare daily total made the projection treat them as a residential schedule, drawing them from
 * the room deposit once cash ran low; a plan that changed nothing then ended below the engine's benchmark.
 * With nothing linked the options and costs are returned as they came.
 */
export function sahProjectionArgs<C extends { totalDailyCost: number }>(args: {
    options: Record<string, unknown>;
    costs: C;
    supportAtHomeAYear: number;
    linkedFeesAYear: number;
}): { options: Record<string, unknown>; costs: C } {
    const fees = Number(args.linkedFeesAYear) || 0;
    if (fees <= 0) return { options: args.options, costs: args.costs };
    const sah = Number(args.supportAtHomeAYear) || 0;
    return {
        options: { ...args.options, linkedAgedCareAnnualCost: fees, integratedAgedCareCost: sah + fees },
        costs: { ...args.costs, totalDailyCost: sah / 365 },
    };
}
